import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, Building2, Globe, Mail, Phone, Linkedin, Loader2, Sparkles, Copy, Ban, Check, Video, Upload, Camera, FileText, QrCode, Send } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { VCardQRButton } from '../components/VCardQRModal';
import OutreachExportDialog from '../components/OutreachExportDialog';

export default function AccountDetailPage() {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatingVideo, setGeneratingVideo] = useState(false);
  const [activeVideoId, setActiveVideoId] = useState(null);
  const [selfieUrl, setSelfieUrl] = useState(null);
  const [uploadingSelfie, setUploadingSelfie] = useState(false);
  const [copied, setCopied] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const fileInputRef = useRef(null);

  const pollRef = useRef(null);

  const loadData = useCallback(async () => {
    try {
      const [accRes, selfieRes, videosRes] = await Promise.all([
        api.get(`/discovery/accounts/${accountId}`),
        api.get('/company/selfie'),
        api.get(`/outreach/videos/${accountId}`),
      ]);
      setAccount(accRes.data.account);
      setDrafts(accRes.data.outreach_drafts || []);
      setSelfieUrl(selfieRes.data.selfie_url);
      setVideos(videosRes.data.videos || []);
      // Check if any video is still generating
      const active = (videosRes.data.videos || []).find(v => v.status === 'generating' || v.status === 'script_ready');
      if (active) {
        setActiveVideoId(active.id);
        startVideoPolling(active.id);
      }
    } catch {
      toast.error('Account nicht gefunden');
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    loadData();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [loadData]);

  const startVideoPolling = (videoId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/outreach/video-status/${videoId}`);
        const d = res.data;
        if (d.status === 'completed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setActiveVideoId(null);
          setGeneratingVideo(false);
          toast.success('Video erfolgreich erstellt!');
          loadData();
        } else if (d.status === 'failed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setActiveVideoId(null);
          setGeneratingVideo(false);
          toast.error('Video-Erstellung fehlgeschlagen: ' + (d.detail || ''));
          loadData();
        }
      } catch {}
    }, 5000);
  };

  const uploadSelfie = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingSelfie(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/company/selfie', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSelfieUrl(res.data.selfie_url);
      toast.success('Selfie hochgeladen!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploadingSelfie(false);
    }
  };

  const generateVideo = async () => {
    if (!selfieUrl) {
      toast.error('Bitte laden Sie zuerst ein Selfie hoch');
      return;
    }
    setGeneratingVideo(true);
    try {
      const res = await api.post(`/outreach/generate-video/${accountId}`);
      const { video_id, script } = res.data;
      setActiveVideoId(video_id);
      setVideos(prev => [{ id: video_id, status: 'generating', script, detail: 'Video wird generiert...' }, ...prev]);
      startVideoPolling(video_id);
      toast.info('Video-Generierung gestartet...');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Video konnte nicht erstellt werden');
      setGeneratingVideo(false);
    }
  };

  const generateOutreach = async () => {
    setGenerating(true);
    try {
      const res = await api.post(`/outreach/generate/${accountId}`);
      setDrafts(d => [res.data.draft, ...d]);
      toast.success('Outreach-Entwurf erstellt!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Outreach-Erstellung fehlgeschlagen');
    } finally {
      setGenerating(false);
    }
  };

  const markOptOut = async () => {
    try {
      await api.post(`/outreach/optout/${accountId}`);
      setAccount(a => ({ ...a, opt_out: true }));
      toast.success('Als "Nicht kontaktieren" markiert');
    } catch {
      toast.error('Opt-Out fehlgeschlagen');
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
    toast.success('In die Zwischenablage kopiert');
  };

  const scoreColor = (score) => score >= 80 ? 'text-emerald-600' : score >= 60 ? 'text-amber-600' : 'text-red-500';

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!account) return <div className="text-center py-16"><p>Account nicht gefunden</p></div>;

  return (
    <div data-testid="account-detail-page">
      <Button variant="ghost" onClick={() => navigate('/discovery')} className="mb-4" data-testid="back-to-discovery-btn">
        <ArrowLeft className="w-4 h-4 mr-2" /> Zurück zur Recherche
      </Button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Company Info */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Building2 className="w-7 h-7 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-xl">{account.company_name}</CardTitle>
                    {account.opt_out && <Badge variant="destructive">Nicht kontaktieren</Badge>}
                  </div>
                    <CardDescription>
                    {account.industry}
                    {(() => {
                      // New: use employees_bucket if available (canonical user-defined buckets)
                      const ba = account.bundesanzeiger || {};
                      const bucket = account.employees_bucket;
                      const hasVerifiedMA = ba.found && ba.employees;
                      if (bucket) {
                        return (
                          <span className={hasVerifiedMA ? 'text-foreground' : 'text-muted-foreground'}>
                            {' · '}{bucket}
                            {hasVerifiedMA && <span className="inline-flex items-center ml-1 text-emerald-600 font-bold" data-testid="ma-verified">&#10003;</span>}
                          </span>
                        );
                      }
                      const maText = hasVerifiedMA ? `${ba.employees} MA` : (account.employee_count ? `~ ${account.employee_count} MA` : null);
                      if (!maText) return null;
                      return (
                        <span className={hasVerifiedMA ? 'text-foreground' : 'text-muted-foreground'}>
                          {' · '}{maText}
                          {hasVerifiedMA && <span className="inline-flex items-center ml-1 text-emerald-600 font-bold" data-testid="ma-verified">&#10003;</span>}
                        </span>
                      );
                    })()}
                    {account.location ? ` · ${account.location}` : ''}
                    {account.location_verified && <span className="inline-flex items-center ml-1 text-emerald-600 text-xs font-bold">&#10003;</span>}
                    {(() => {
                      const rd = account.revenue_data || {};
                      const ba = account.bundesanzeiger || {};
                      const fmtEur = (eur) => eur >= 1_000_000_000
                        ? `${(eur/1_000_000_000).toFixed(2).replace(/\.?0+$/,'')} Mrd €`
                        : eur >= 1_000_000
                          ? `${(eur/1_000_000).toFixed(1).replace(/\.0$/,'')} Mio €`
                          : `${(eur/1000).toFixed(0)}k €`;

                      // Render checkmark based on confidence: primary=green, secondary=amber, estimate=none
                      const renderCheck = (confidence, title) => {
                        if (confidence === 'primary') {
                          return <span className="inline-flex items-center ml-1 text-emerald-600 font-bold" title={title || 'Primärquelle (Bundesanzeiger / Unternehmensregister)'} data-testid="rev-verified-primary">&#10003;</span>;
                        }
                        if (confidence === 'secondary') {
                          return <span className="inline-flex items-center ml-1 text-amber-500 font-bold" title={title || 'Sekundärquelle (Branchenverzeichnis / Wirtschaftspresse)'} data-testid="rev-verified-secondary">&#10003;</span>;
                        }
                        return null;
                      };

                      // 1) New revenue_data with confidence
                      if (rd.verified && rd.value_eur) {
                        const fyText = rd.fiscal_year ? ` (FY ${rd.fiscal_year})` : '';
                        const conf = rd.confidence || 'secondary';
                        return (
                          <span>
                            {' · '}<span className={conf === 'primary' ? 'text-foreground font-medium' : 'text-foreground'}>{fmtEur(rd.value_eur)}{fyText}</span>
                            {renderCheck(conf, rd.source_label)}
                          </span>
                        );
                      }
                      // 2) Legacy bundesanzeiger (treat as primary)
                      if (ba.found && ba.revenue_eur) {
                        const fyText = ba.fiscal_year ? ` (FY ${ba.fiscal_year})` : '';
                        return (
                          <span>
                            {' · '}<span className="text-foreground font-medium">{fmtEur(ba.revenue_eur)}{fyText}</span>
                            {renderCheck('primary', 'Bundesanzeiger')}
                          </span>
                        );
                      }
                      // 3) Tight estimate band — KEIN Haken, nur ~
                      if (rd.estimate_low && rd.estimate_high) {
                        return (
                          <span className="text-muted-foreground" title={rd.basis || ''}>
                            {' · ~ '}{fmtEur(rd.estimate_low)}–{fmtEur(rd.estimate_high)}
                          </span>
                        );
                      }
                      if (account.revenue) {
                        return <span className="text-muted-foreground">{' · ~ '}{account.revenue}</span>;
                      }
                      return null;
                    })()}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Match Reasoning - warum passt das Unternehmen */}
              {account.match_reasoning && (
                <div className="mb-4 p-3 rounded-lg bg-primary/5 border border-primary/10">
                  <p className="text-xs font-medium text-primary mb-1">Warum passt dieses Unternehmen?</p>
                  <p className="text-sm">{account.match_reasoning}</p>
                </div>
              )}
              {account.description && <p className="text-sm text-muted-foreground mb-4">{account.description}</p>}
              {(account.fit_score > 0 || account.need_score > 0 || account.reachability_score > 0) ? (
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { label: 'Passung', score: account.fit_score }, 
                    { label: 'Bedarf', score: account.need_score }, 
                    { label: 'Erreichbarkeit', score: account.reachability_score }
                  ].map(s => (
                    <div key={s.label} className="text-center p-3 rounded-lg border border-border/50">
                      <p className="text-xs text-muted-foreground mb-1">{s.label}</p>
                      <p className={`text-2xl font-bold ${scoreColor(s.score)}`}>{s.score}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground text-center py-2">Detaillierte Scores werden bei neuen Suchen berechnet</p>
              )}
            </CardContent>
          </Card>

          {/* Video Outreach — ausgeblendet, wird später als kostenpflichtiges Feature reaktiviert */}

          {/* Text Outreach */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-lg">Outreach-Entwürfe</CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setExportOpen(true)} disabled={account.opt_out} data-testid="open-outreach-export-btn">
                  <Send className="w-4 h-4 mr-2" /> E-Mail vorbereiten
                </Button>
                <Button onClick={generateOutreach} disabled={generating || account.opt_out} data-testid="generate-outreach-btn">
                  {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  KI-Entwurf erstellen
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {drafts.length === 0 ? (
                <div className="text-center py-8">
                  <Sparkles className="w-8 h-8 text-muted-foreground/40 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">Noch keine Entwürfe. Erstellen Sie einen, um loszulegen.</p>
                </div>
              ) : (
                <Tabs defaultValue="0" className="space-y-4">
                  <TabsList>
                    {drafts.map((_, i) => <TabsTrigger key={i} value={String(i)} data-testid={`draft-tab-${i}`}>Entwurf {i + 1}</TabsTrigger>)}
                  </TabsList>
                  {drafts.map((draft, i) => (
                    <TabsContent key={i} value={String(i)} className="space-y-4">
                      <div className="p-4 rounded-lg border border-border/50">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-medium">E-Mail</p>
                          <Button variant="ghost" size="sm" onClick={() => copyToClipboard(`Betreff: ${draft.email_subject}\n\n${draft.email_body}`, `email-${i}`)} data-testid={`copy-email-${i}`}>
                            {copied === `email-${i}` ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          </Button>
                        </div>
                        <p className="text-sm font-medium mb-1">Betreff: {draft.email_subject}</p>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">{draft.email_body}</p>
                      </div>
                      <div className="p-4 rounded-lg border border-border/50">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-medium">Anruf-Skript</p>
                          <Button variant="ghost" size="sm" onClick={() => copyToClipboard(draft.call_script, `call-${i}`)} data-testid={`copy-call-${i}`}>
                            {copied === `call-${i}` ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          </Button>
                        </div>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">{draft.call_script}</p>
                      </div>
                      {draft.key_talking_points?.length > 0 && (
                        <div className="p-4 rounded-lg border border-border/50">
                          <p className="text-sm font-medium mb-2">Gesprächspunkte</p>
                          <ul className="space-y-1.5">
                            {draft.key_talking_points.map((p, j) => <li key={j} className="text-sm text-muted-foreground flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0 mt-1.5" />{p}</li>)}
                          </ul>
                        </div>
                      )}
                    </TabsContent>
                  ))}
                </Tabs>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-lg">Matching-Score</CardTitle></CardHeader>
            <CardContent>
              <div className="text-center mb-4">
                <div className="relative w-24 h-24 mx-auto">
                  <svg className="w-24 h-24 -rotate-90" viewBox="0 0 36 36">
                    <circle cx="18" cy="18" r="16" fill="none" stroke="hsl(var(--muted))" strokeWidth="2" />
                    <circle cx="18" cy="18" r="16" fill="none" stroke="hsl(var(--primary))" strokeWidth="2" strokeLinecap="round"
                      strokeDasharray={`${account.match_score} ${100 - account.match_score}`} />
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center text-2xl font-bold">{account.match_score}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Kontaktdaten</CardTitle>
                <VCardQRButton lead={account} variant="outline" size="sm" className="h-8 gap-1.5 text-xs" />
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {account.website ? (
                <a href={account.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-primary hover:underline" data-testid="account-website-link">
                  <Globe className="w-4 h-4" /> {account.website}
                </a>
              ) : (
                <div className="flex items-center gap-2 text-sm text-muted-foreground italic" data-testid="account-no-website">
                  <Globe className="w-4 h-4" /> – keine Webseite vorhanden –
                </div>
              )}
              {account.contact_info?.general_email && (
                <a href={`mailto:${account.contact_info.general_email}`} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="account-email-link">
                  <Mail className="w-4 h-4" /> {account.contact_info.general_email}
                </a>
              )}
              {(account.contact_info?.general_phone || account.contact_info?.contact_phone || account.contact_info?.phone) && (
                <a href={`tel:${account.contact_info.general_phone || account.contact_info.contact_phone || account.contact_info.phone}`} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="account-phone-link">
                  <Phone className="w-4 h-4" /> {account.contact_info.general_phone || account.contact_info.contact_phone || account.contact_info.phone}
                </a>
              )}
              
              {/* Ansprechpartner Section */}
              {(account.contact_info?.contact_person || account.contact_info?.contact_position) && (
                <div className="pt-3 mt-3 border-t border-border/50">
                  <p className="text-xs text-muted-foreground mb-2">Ansprechpartner</p>
                  <div className="space-y-1">
                    {account.contact_info?.contact_person && (
                      <p className="text-sm font-medium" data-testid="contact-person">{account.contact_info.contact_person}</p>
                    )}
                    {account.contact_info?.contact_position && (
                      <p className="text-xs text-muted-foreground" data-testid="contact-position">{account.contact_info.contact_position}</p>
                    )}
                    {account.contact_info?.contact_phone && (
                      <a href={`tel:${account.contact_info.contact_phone}`} className="flex items-center gap-2 text-xs text-primary hover:underline" data-testid="contact-phone-link">
                        <Phone className="w-3 h-3" /> {account.contact_info.contact_phone}
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Einkauf / Beschaffung Kontakt */}
              {account.purchasing_contact && (account.purchasing_contact.name || account.purchasing_contact.position) && (
                <div className="pt-3 mt-3 border-t border-border/50">
                  <p className="text-xs text-muted-foreground mb-2">Kontakt Einkauf / Beschaffung</p>
                  <div className="space-y-1">
                    {account.purchasing_contact.name && (
                      <p className="text-sm font-medium" data-testid="purchasing-contact-name">{account.purchasing_contact.name}</p>
                    )}
                    {account.purchasing_contact.position && (
                      <p className="text-xs text-muted-foreground" data-testid="purchasing-contact-position">{account.purchasing_contact.position}</p>
                    )}
                    {account.purchasing_contact.phone && (
                      <a href={`tel:${account.purchasing_contact.phone}`} className="flex items-center gap-2 text-xs text-primary hover:underline" data-testid="purchasing-contact-phone">
                        <Phone className="w-3 h-3" /> {account.purchasing_contact.phone}
                      </a>
                    )}
                    {account.purchasing_contact.email && (
                      <a href={`mailto:${account.purchasing_contact.email}`} className="flex items-center gap-2 text-xs text-primary hover:underline" data-testid="purchasing-contact-email">
                        <Mail className="w-3 h-3" /> {account.purchasing_contact.email}
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Geschäftsführer / Inhaber aus Impressum */}
              {account.impressum && (account.impressum.managing_director || account.impressum.email) && (
                <div className="pt-3 mt-3 border-t border-border/50" data-testid="impressum-section">
                  <p className="text-xs text-muted-foreground mb-2">Laut Impressum</p>
                  <div className="space-y-1">
                    {account.impressum.managing_director && (
                      <p className="text-sm font-medium" data-testid="impressum-director">
                        {account.impressum.managing_director}
                      </p>
                    )}
                    {account.impressum.email && (
                      <a href={`mailto:${account.impressum.email}`} className="flex items-center gap-2 text-xs text-primary hover:underline" data-testid="impressum-email">
                        <Mail className="w-3 h-3" /> {account.impressum.email}
                      </a>
                    )}
                    {account.impressum.source_url && (
                      <a href={account.impressum.source_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[10px] text-muted-foreground hover:text-foreground italic underline" data-testid="impressum-source">
                        Quelle: Impressum
                      </a>
                    )}
                  </div>
                </div>
              )}
              
              {account.contact_info?.linkedin && (
                <a href={account.contact_info.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="account-linkedin-link">
                  <Linkedin className="w-4 h-4" /> LinkedIn
                </a>
              )}
            </CardContent>
          </Card>

          {!account.opt_out && (
            <Button variant="outline" className="w-full text-destructive hover:bg-destructive/10" onClick={markOptOut} data-testid="optout-btn">
              <Ban className="w-4 h-4 mr-2" /> Nicht kontaktieren
            </Button>
          )}
        </div>
      </div>

      <OutreachExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        accountId={accountId}
        accountName={account.company_name}
        hasAIDraft={drafts.length > 0}
      />
    </div>
  );
}
